import Image from 'next/image'
import { cn } from '@/utils/twMerge'
import React, { useCallback } from 'react'
import PaginationButtons from './PaginationButtons'

type PaginationProps = {
  currentPage: number
  maxPage: number
  nextPage: () => void
  prevPage: () => void
  goToPage: (page: number) => void
}

const Pagination = React.memo(function Pagination({
  currentPage,
  maxPage,
  goToPage,
  nextPage,
  prevPage,
}: PaginationProps) {
  const handlePrevPage = useCallback(() => prevPage(), [prevPage])
  const handleNextPage = useCallback(() => nextPage(), [nextPage])

  return (
    <nav className="mt-16 flex items-center justify-center" aria-label="Pagination">
      <button
        type="button"
        onClick={handlePrevPage}
        disabled={currentPage === 1}
        aria-label="Go to previous page"
        className={cn(
          'group flex h-12 w-12 items-center justify-center rounded border border-secondary-gray-150 bg-secondary-gray-100',
          currentPage === 1
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-primary-green-85 hover:bg-primary-green-85',
        )}>
        <Image
          src="/images/arrow_forward.svg"
          width={8}
          height={10}
          alt=""
          aria-hidden="true"
          className={cn('rotate-180', currentPage !== 1 && 'group-hover:invert')}
        />
      </button>

      <div className="mx-2 flex gap-x-2">
        <PaginationButtons currentPage={currentPage} maxPage={maxPage} goToPage={goToPage} />
      </div>

      <button
        type="button"
        onClick={handleNextPage}
        disabled={currentPage === maxPage}
        aria-label="Go to next page"
        className={cn(
          'group flex h-12 w-12 items-center justify-center rounded border border-secondary-gray-150 bg-secondary-gray-100',
          currentPage === maxPage
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-primary-green-85 hover:bg-primary-green-85',
        )}>
        <Image
          src="/images/arrow_forward.svg"
          width={8}
          height={10}
          alt=""
          aria-hidden="true"
          className={cn(currentPage !== maxPage && 'group-hover:invert')}
        />
      </button>
    </nav>
  )
})

export default Pagination
